import { useEffect, useMemo, useState } from 'react'
import { HTTP_STATUS } from '../../domain/domainCodes.js'
import { useNavigate, useParams } from 'react-router-dom'
import { GraduationCap } from 'lucide-react'
import FormPage from '../../components/FormPage'
import { FormCols, FormSection, InfoNote, SwitchField, Textarea } from '../../components/Form'
import FormRow from '../../components/FormRow'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import PageLoader from '../../components/PageLoader'
import { ErrorState, NotFoundState } from '../../components/States'
import { useToast } from '../../components/toastContext'
import { PROGRAM_BASICS_MAX, getProgram, listPrograms, saveProgram } from './programsService'
import { nameClashes, takenNames } from './programNames'
import { strings as S } from './strings'
import { serverDetail, serverText } from '../../lib/i18n'
import { hasLetter } from '../../lib/name'

const EMPTY = {
  arabicName: '',
  arabicDescription: '',
  englishName: '',
  englishDescription: '',
  pastCoursesView: 'show',
  futureCoursesView: 'show',
  allowAddingCoursesAfterExamination: false,
}

const asOptions = (map) => Object.entries(map).map(([value, label]) => ({ value, label }))

/**
 * برنامج تدريبي جديد / تعديل — Views/ProgramsManagement/_ProgramManagement_Program_Modal.
 *
 * The legacy's fields, unchanged: two names, two descriptions, the two
 * course-visibility rules, and the one switch that changes what happens to
 * people who have already finished.
 *
 * What changed is the container. This was a 560px side sheet with all seven
 * controls in one column; it is a page now, and the two questions it actually
 * asks — what the programme IS, and what a trainee sees of it — are two steps
 * rather than two headings you scroll past. The legacy stacks its visibility
 * selects under the text fields with nothing marking them as a different kind
 * of decision at all.
 */
export default function ProgramFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(!!id)
  const [saving, setSaving] = useState(false)
  /* An id that matches no programme. The form used to open blank for it, and saving that
     blank form made a NEW programme (Taiga #1547). */
  const [missing, setMissing] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [resolvedId, setResolvedId] = useState(null)
  const [step, setStep] = useState(0)
  /* The other programmes' names, so a taken one is said on its field at step 1 rather than
     as an anonymous refusal after the last step (Taiga #1530). */
  const [taken, setTaken] = useState(null)

  const loadTaken = () =>
    listPrograms()
      .then((list) => takenNames(list.rows, id))
      .catch(() => null)

  useEffect(() => {
    let alive = true
    listPrograms()
      .then((list) => alive && setTaken(takenNames(list.rows, id)))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [id])

  useEffect(() => {
    setMissing(false)
    setLoadError(false)
    setValues(EMPTY)
    setErrors({})
    setStep(0)
    if (!id) {
      setLoading(false)
      setResolvedId(null)
      return
    }
    setLoading(true)
    let alive = true
    getProgram(id)
      .then((program) => {
        if (!alive) return
        if (!program) setMissing(true)
        setValues({ ...EMPTY, ...program })
        setResolvedId(id)
        setLoading(false)
      })
      .catch((error) => {
        if (!alive) return
        setMissing(error?.status === HTTP_STATUS.NOT_FOUND)
        setLoadError(error?.status !== HTTP_STATUS.NOT_FOUND)
        setResolvedId(id)
        setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [id])

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }

  const back = () => navigate('/programs')

  const steps = useMemo(
    () => [
      { id: 'identity', label: S.form.steps.identity },
      { id: 'visibility', label: S.form.steps.visibility },
    ],
    [],
  )

  /* All four text fields are required, and capped at the length the server stores. */
  const validateStep = (index) => {
    if (index > 0) return true
    const next = {}
    for (const [key, max] of Object.entries(PROGRAM_BASICS_MAX)) {
      const value = values[key].trim()
      if (!value) next[key] = S.form.required
      else if (value.length > max) next[key] = S.form.tooLong(max)
    }
    /* The server's rule (NameText): "..." or "123" satisfies `required` and is not a name. */
    for (const key of ['arabicName', 'englishName']) {
      if (!next[key] && !hasLetter(values[key])) next[key] = S.form.nameLetters
    }
    const clashes = nameClashes(values, taken, S.form.nameTaken)
    for (const key of Object.keys(clashes)) next[key] ??= clashes[key]
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const submit = async () => {
    if (loading || missing || loadError || resolvedId !== (id ?? null) || (id && String(values.id) !== String(id))) return
    setSaving(true)
    /* The seven fields this form owns, and only those. `values` also holds the group
       assignment and certificate settings the row was loaded with, and posting those back
       from the NAME form would write them again — with whatever happened to be loaded. */
    const outcome = await saveProgram({
      id: values.id,
      arabicName: values.arabicName,
      arabicDescription: values.arabicDescription,
      englishName: values.englishName,
      englishDescription: values.englishDescription,
      pastCoursesView: values.pastCoursesView,
      futureCoursesView: values.futureCoursesView,
      allowAddingCoursesAfterExamination: values.allowAddingCoursesAfterExamination,
    })
    if (!outcome.ok) {
      setSaving(false)
      /* Taken since this form loaded the list: back to the names, on the field that clashes. */
      const fresh = await loadTaken()
      if (fresh) setTaken(fresh)
      const clashes = nameClashes(values, fresh, S.form.nameTaken)
      if (Object.keys(clashes).length) {
        setErrors(clashes)
        return setStep(0)
      }
      return toast.error(serverDetail(outcome.message) || S.fail.save)
    }
    toast.success(serverText(outcome.message) || S.form.saved)
    navigate('/programs')
  }

  if (loading || resolvedId !== (id ?? null)) return <PageLoader />
  if (missing) return <NotFoundState home="/programs" />
  if (loadError) return <ErrorState title={S.fail.detail} />

  return (
    <FormPage
      icon={GraduationCap}
      title={id ? S.form.editTitle : S.actions.create}
      subtitle={S.title}
      steps={steps}
      step={step}
      onStep={setStep}
      validateStep={validateStep}
      onBack={back}
      onSubmit={submit}
      submitLabel={S.form.save}
      submitting={saving}
    >
      {({ step }) =>
        step === 0 ? (
          <FormSection>
            <FormCols>
              <FormRow label={S.form.arabicName} error={errors.arabicName}>
                {(fid) => (
                  <TextField
                    id={fid}
                    value={values.arabicName}
                    onChange={set('arabicName')}
                    invalid={!!errors.arabicName}
                    autoComplete="off"
                  />
                )}
              </FormRow>

              <FormRow label={S.form.englishName} error={errors.englishName}>
                {(fid) => (
                  <TextField
                    id={fid}
                    dir="ltr"
                    value={values.englishName}
                    onChange={set('englishName')}
                    invalid={!!errors.englishName}
                    autoComplete="off"
                  />
                )}
              </FormRow>

              <FormRow label={S.form.arabicDescription} error={errors.arabicDescription}>
                {(fid) => (
                  <Textarea
                    id={fid}
                    rows={5}
                    value={values.arabicDescription}
                    onChange={set('arabicDescription')}
                    invalid={!!errors.arabicDescription}
                  />
                )}
              </FormRow>

              <FormRow label={S.form.englishDescription} error={errors.englishDescription}>
                {(fid) => (
                  <Textarea
                    id={fid}
                    dir="ltr"
                    rows={5}
                    value={values.englishDescription}
                    onChange={set('englishDescription')}
                    invalid={!!errors.englishDescription}
                  />
                )}
              </FormRow>
            </FormCols>
          </FormSection>
        ) : (
          <FormSection>
            <FormCols>
              <FormRow label={S.form.pastCourses}>
                {(fid) => (
                  <Select
                    id={fid}
                    value={values.pastCoursesView}
                    onChange={set('pastCoursesView')}
                    options={asOptions(S.form.pastViews)}
                  />
                )}
              </FormRow>

              <FormRow label={S.form.futureCourses}>
                {(fid) => (
                  <Select
                    id={fid}
                    value={values.futureCoursesView}
                    onChange={set('futureCoursesView')}
                    options={asOptions(S.form.futureViews)}
                  />
                )}
              </FormRow>
            </FormCols>

            <SwitchField
              checked={values.allowAddingCoursesAfterExamination}
              onChange={set('allowAddingCoursesAfterExamination')}
              label={S.form.allowLateCourses}
            />

            {/* The legacy prints this banner unconditionally. It only says
                anything while the switch is on, so that is when it appears. */}
            {values.allowAddingCoursesAfterExamination && (
              <InfoNote>
                <bdi>{S.form.allowLateCoursesNote}</bdi>
              </InfoNote>
            )}
          </FormSection>
        )
      }
    </FormPage>
  )
}
